import Resource from 'Resources/Resource';

export type QuicksearchType =
	| 'client'
	| 'contact'
	| 'subAccount'
	| 'activity'
	| 'appointment'
	| 'opportunity'
	| 'order';

/** Quicksearch rows are raw API rows — no mapper runs over them, see the class comment. */
export type QuicksearchRow = { id: number; [key: string]: any };

export type QuicksearchEntity = {
	data: QuicksearchRow[];
	metadata: { total: number; limit: number; offset: number };
	type: QuicksearchType;
	sortOrder: number;
};

/**
 * A group is only present when the account has the feature that unlocks it (see
 * `searchAll` in `base/src/core/helper/quickSearchHelper.js`) and the caller asked for
 * the type, so every read has to tolerate a missing key. `ts` is echoed back so a caller
 * can drop out-of-order responses.
 */
export type QuicksearchResults = Partial<Record<QuicksearchType, QuicksearchEntity>> & { ts?: number };

/** Decides which group is offered first when several types match. */
const sortOrder: Record<QuicksearchType, number> = {
	client: 1,
	contact: 2,
	subAccount: 3,
	activity: 4,
	appointment: 5,
	opportunity: 6,
	order: 7
};

const MIN_SEARCH_LENGTH = 2;

/**
 * The `/quicksearch` endpoint behind the navbar supersearch.
 *
 * This goes through `_getRequest` rather than `find()` because the envelope's `data` is an
 * object keyed by type, not an array — `find()`'s `handleDataArray` would `.map` over it and
 * throw. Nothing is lost by skipping it: with no attributes and no `dateFields`, the parser
 * `find()` would apply is the identity, which is also why quicksearch rows have always
 * carried raw date strings (the Angular resource it replaced passed `skipDateConvert: true`
 * with a `null` parser) and why the result rows are written against that.
 */
class Quicksearch extends Resource {
	constructor() {
		super('quicksearch');
	}

	/**
	 * `q` is stringified here rather than left to the serializer: the controller does
	 * `q.map(decodeJson)` and answers 400 (`Metadata.InvalidFilterQuery`) on a `q` that
	 * isn't a JSON string. axios happens to stringify object params the same way today, so
	 * this is belt-and-braces — it keeps the encoding correct if a `paramsSerializer` is
	 * ever added, and it matches how ui-3 builds the same call.
	 *
	 * Retries are off because the shared axios instance has `axiosRetry` installed with
	 * three attempts. Supersearch fires one of these per typing pause and discards any
	 * response whose `ts` is stale, so a retried keystroke is guaranteed-wasted load on an
	 * expensive endpoint — and the Angular `$resource` this replaced never retried either.
	 */
	search(searchString: string, ts?: number, options: Record<string, string | number> = {}) {
		if (searchString.length < MIN_SEARCH_LENGTH) {
			return Promise.resolve<QuicksearchResults>({});
		}

		return this._getRequest('', {
			methodName: 'search',
			'axios-retry': { retries: 0 },
			params: { q: JSON.stringify({ a: 'searchValue', c: 'eq', v: searchString }), ...options }
		}).then((res: { data: { data?: Record<string, Omit<QuicksearchEntity, 'type' | 'sortOrder'>> } }) => {
			const result: QuicksearchResults = {};

			if (ts) {
				result.ts = ts;
			}

			for (const [key, group] of Object.entries(res.data.data ?? {})) {
				const type = key as QuicksearchType;
				result[type] = { ...group, type, sortOrder: sortOrder[type] };
			}

			return result;
		});
	}
}

const resource = new Quicksearch();

export default resource;
